import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Grid, 
  CircularProgress, 
  Alert, 
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { apiService } from '../services/api.service';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const LogoUpload = () => {
  const { tenantAlias } = useParams();
  const navigate = useNavigate();
  const [logoType, setLogoType] = useState('primary');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Helper function to extract error message
  const getErrorMessage = (error) => {
    if (typeof error === 'string') return error;
    if (error.response?.data?.detail) return error.response.data.detail;
    if (error.message) return error.message;
    if (typeof error === 'object') {
      // Handle validation error object
      if (error.msg) return error.msg;
      if (error.type) return `Error type: ${error.type}`;
      return 'An unexpected error occurred';
    }
    return 'An unexpected error occurred';
  };
  
  // Configure dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg']
    },
    maxSize: 5242880, // 5MB
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setPreview(URL.createObjectURL(file));
        setSelectedFile(file);
        setError(null);
      }
    },
    onDropRejected: (rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors[0].code === 'file-too-large') {
          setError('File is too large. Maximum size is 5MB.');
        } else {
          setError(getErrorMessage(rejection.errors[0]));
        }
      }
    }
  });
  
  const handleLogoTypeChange = (event) => {
    setLogoType(event.target.value);
  };
  
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(false);
      
      // Create FormData correctly
      const formData = new FormData();
      formData.append('logo', selectedFile);  // 'logo' matches the FastAPI parameter name
      formData.append('logo_type', logoType);
      
      // Log the form data for debugging
      console.log('Uploading file:', {
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        logoType: logoType,
        size: selectedFile.size
      });

      // Use apiService with correct content type
      const response = await apiService.post(`/ux/logos/${tenantAlias}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'  // Let browser set the correct boundary
        }
      });
      
      console.log('Upload response:', response);
      
      setSuccess(true);
      setSelectedFile(null);
      setPreview(null);
    } catch (err) {
      console.error('Failed to upload logo:', err);
      // Handle specific error cases
      if (err.response?.status === 422) {
        setError('Invalid file or logo type. Please check your selection.');
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate(`/developers/tenant/${tenantAlias}`)}
          sx={{ mr: 2 }}
        >
          Back to Tenant
        </Button>
        <Typography variant="h4">Upload Logo for {tenantAlias}</Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {getErrorMessage(error)}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Logo uploaded successfully!
        </Alert>
      )}
      
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="logo-type-label">Logo Type</InputLabel>
              <Select
                labelId="logo-type-label"
                value={logoType}
                onChange={handleLogoTypeChange}
                label="Logo Type"
              >
                <MenuItem value="primary">Primary Logo</MenuItem>
                <MenuItem value="symbol">Symbol Only</MenuItem>
                <MenuItem value="favicon">Favicon</MenuItem>
              </Select>
            </FormControl>
            
            <Box 
              {...getRootProps()} 
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: isDragActive ? 'rgba(0, 163, 224, 0.1)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(0, 163, 224, 0.05)'
                }
              }}
            >
              <input {...getInputProps()} />
              {uploading ? (
                <CircularProgress />
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 48, color: '#1A2942', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Drag & drop logo here, or click to select
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Supported formats: PNG, JPG, SVG. Max size: 5MB
                  </Typography>
                </>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Preview
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {preview ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box 
                  component="img" 
                  src={preview} 
                  alt="Logo Preview"
                  sx={{ 
                    maxWidth: '100%', 
                    maxHeight: '200px',
                    mb: 2
                  }} 
                />
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {logoType === 'primary' ? 'Primary Logo' : 
                   logoType === 'symbol' ? 'Symbol Only' : 'Favicon'}
                </Typography>
                
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpload}
                  disabled={uploading || !selectedFile}
                  startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                >
                  {uploading ? 'Uploading...' : 'Upload Logo'}
                </Button>
              </Box>
            ) : (
              <Box 
                sx={{ 
                  height: '200px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  border: '1px solid #eee',
                  borderRadius: 1
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  No logo selected
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default LogoUpload; 